import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import auth from "../services/auth";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const API = "https://ellinornielsen-backend.onrender.com/api";

const C = {
  bg: "#f5f0eb",
  sidebar: "#815641",
  accent: "#815641",
  accentLight: "#a06535",
  accentFaint: "rgba(129,86,65,0.10)",
  card: "#ffffff",
  border: "rgba(129,86,65,0.15)",
  text: "#2c1f15",
  muted: "#9a7c6a",
  green: "#2e8b57",
  red: "#c0392b",
  yellow: "#b07d2a",
  blue: "#4a90a4",
};

function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("bokningar");

  // ── Bokningar ──
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState([]);
  const [nekadStats, setNekadStats] = useState([]);

  // ── Media ──
  const [mediaActive, setMediaActive] = useState([]);
  const [mediaInactive, setMediaInactive] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadText, setUploadText] = useState("");
  const [uploadPreviewImg, setUploadPreviewImg] = useState(null);
  const [uploadPreviewVid, setUploadPreviewVid] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [editCaptionId, setEditCaptionId] = useState(null);
  const [editCaptionText, setEditCaptionText] = useState("");

  // ── Citat ──
  const [citat, setCitat] = useState([]);
  const [citatText, setCitatText] = useState("");
  const [citatAction, setCitatAction] = useState("send");
  const [citatEditId, setCitatEditId] = useState(null);

  // ── Låtar ──
  const [music, setMusic] = useState([]);
  const [musicCat, setMusicCat] = useState("Bröllop");
  const [musicAction, setMusicAction] = useState("send");
  const [musicEditId, setMusicEditId] = useState(null);
  const [musicForm, setMusicForm] = useState({ song: "", artist: "", link: "", category: "Bröllop", fil: "" });
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // ── Kalender ──
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [expandedPos, setExpandedPos] = useState({ x: 0, y: 0 });
  const [calTimeEdit, setCalTimeEdit] = useState({}); // { [bookingId]: { from, to } }

  // ── Platser ──
  const [maps, setMaps] = useState([]);
  const [mapForm, setMapForm] = useState({ latitude: "", longitude: "", destination: "" });

  // ── Statistik ──
  const [visitorStats, setVisitorStats] = useState(null);

  useEffect(() => {
    (async () => {
      const u = await auth.getCurrentUser();
      if (!u) return navigate("/login");
      setUser(u);
      fetchAll();
    })();
  }, []);

  function fetchAll() {
    fetchBookings();
    fetchStats();
    fetchNekadStats();
    fetchMedia();
    fetchCitat();
    fetchMusic();
    fetchMaps();
    fetchVisitorStats();
  }

  async function fetchVisitorStats() {
    const { data } = await axios.get(`${API}/visitors/stats`);
    setVisitorStats(data);
  }

  // ── BOOKINGS ──
  async function fetchBookings() {
    const { data } = await axios.get(`${API}/bokning`);
    setBookings(data);
  }
  async function fetchStats() {
    const { data } = await axios.get(`${API}/stats`);
    setStats(data);
  }
  async function fetchNekadStats() {
    const { data } = await axios.get(`${API}/nekadebokningar`);
    setNekadStats(data);
  }
  async function setBookingStatus(b, status) {
    const { _id, __v, ...rest } = b;
    await axios.put(`${API}/bokning/${b._id}`, { ...rest, isDone: status });
    fetchBookings();
  }
  async function deleteBooking(b) {
    if (b.isDone === true) {
      await axios.post(`${API}/stats`, {
        firstName: b.förnamn, surName: b.efternamn, email: b.epost,
        adress: b.adress, telefon: b.telefon, date: b.datum,
        timeStart: b.tidFrån, timeEnd: b.tidTill,
      });
    } else if (b.isDone === null || b.isDone === undefined) {
      await axios.post(`${API}/nekadebokningar`, { date: b.datum });
    }
    await axios.delete(`${API}/bokning/${b._id}`);
    fetchBookings();
    fetchStats();
    fetchNekadStats();
  }

  // ── MEDIA ──
  async function fetchMedia() {
    const [orderRes, uploadsRes] = await Promise.all([
      axios.get(`${API}/order`),
      axios.get(`${API}/uploads`),
    ]);
    const ordered = orderRes.data[0]?.image || [];
    const all = uploadsRes.data;
    const uploadsById = {};
    const uploadsMapByFilename = {};
    all.forEach((u) => { uploadsById[u._id] = u; uploadsMapByFilename[u.filename] = u; });
    const enriched = ordered.map((item) => {
      const upload = uploadsById[item.filename] || uploadsMapByFilename[item.filename] || uploadsById[item._id];
      return { ...item, _realFilename: upload?.filename || item.filename, text: upload?.text ?? item.text };
    });
    const activeFilenames = new Set(enriched.map((i) => i._realFilename));
    const inactive = all.filter((i) => !activeFilenames.has(i.filename));
    setMediaActive(enriched);
    setMediaInactive(inactive);
  }
  function handleUploadFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    setUploadFile(f);
    if (f.type === "video/mp4") {
      setUploadPreviewVid(URL.createObjectURL(f));
      setUploadPreviewImg(null);
    } else {
      setUploadPreviewImg(URL.createObjectURL(f));
      setUploadPreviewVid(null);
    }
  }
  async function submitUpload(e) {
    e.preventDefault();
    if (!uploadFile) return;
    setUploadLoading(true);
    const fd = new FormData();
    fd.append("photo", uploadFile);
    fd.append("text", uploadText);
    await axios.post(`${API}/uploads`, fd, { headers: { "content-type": "multipart/form-data" } });
    setUploadFile(null);
    setUploadText("");
    setUploadPreviewImg(null);
    setUploadPreviewVid(null);
    setUploadLoading(false);
    fetchMedia();
  }
  async function deleteMedia(item) {
    await axios.delete(`${API}/uploads/${item._realFilename || item.filename}`);
    const newActive = mediaActive.filter((i) => (i._realFilename || i.filename) !== (item._realFilename || item.filename));
    await axios.post(`${API}/order`, newActive);
    fetchMedia();
  }
  async function saveCaption(item) {
    await axios.put(`${API}/uploads/${item._realFilename || item.filename}`, { text: editCaptionText }, { headers: { "Content-Type": "application/json" } });
    const { data } = await axios.get(`${API}/uploads`);
    await axios.post(`${API}/order`, data);
    setEditCaptionId(null);
    fetchMedia();
  }
  function handleDragEnd(result) {
    if (!result.destination) return;
    const src = result.source;
    const dst = result.destination;
    if (src.droppableId === dst.droppableId && src.index === dst.index) return;

    let active = [...mediaActive];
    let inactive = [...mediaInactive];
    let moved;

    if (src.droppableId === "active") moved = active.splice(src.index, 1)[0];
    else moved = inactive.splice(src.index, 1)[0];

    if (dst.droppableId === "active") active.splice(dst.index, 0, moved);
    else inactive.splice(dst.index, 0, moved);

    setMediaActive(active);
    setMediaInactive(inactive);
    axios.post(`${API}/order`, active);
  }

  // ── CITAT ──
  async function fetchCitat() {
    const { data } = await axios.get(`${API}/citat`);
    setCitat(data);
  }
  async function submitCitat() {
    if (!citatText.trim()) return;
    if (citatAction === "send") {
      await axios.post(`${API}/citat`, { text: citatText });
    } else {
      await axios.put(`${API}/citat/${citatEditId}`, { text: citatText });
      setCitatAction("send");
      setCitatEditId(null);
    }
    setCitatText("");
    fetchCitat();
  }
  async function deleteCitat(c) {
    await axios.delete(`${API}/citat/${c._id}`);
    fetchCitat();
  }
  function editCitat(c) {
    setCitatAction("put");
    setCitatEditId(c._id);
    setCitatText(c.text);
  }
  function cancelCitatEdit() {
    setCitatAction("send");
    setCitatEditId(null);
    setCitatText("");
  }

  // ── LÅTAR ──
  async function fetchMusic() {
    const { data } = await axios.get(`${API}/musictext`);
    setMusic(data);
  }
  function handleMusicFormChange(e) {
    const { name, value, files } = e.target;
    if (name === "fil") {
      const f = files?.[0];
      if (f && (f.type.startsWith("video/") || f.type === "audio/mp3" || f.type === "audio/mpeg")) {
        setMusicForm((p) => ({ ...p, fil: f }));
      }
    } else {
      setMusicForm((p) => ({ ...p, [name]: value }));
    }
  }
  async function submitMusic(e) {
    e.preventDefault();
    const config = { headers: { "content-type": "multipart/form-data" } };
    const fd = new FormData();
    fd.append("song", musicForm.song);
    fd.append("artist", musicForm.artist);
    if (musicForm.link) fd.append("link", musicForm.link);
    fd.append("category", musicForm.category);
    if (musicForm.fil) fd.append("media", musicForm.fil);

    if (musicAction === "send") {
      try {
        await axios.post(`${API}/musictext`, fd, config);
        setMusicForm({ song: "", artist: "", link: "", category: "Bröllop", fil: "" });
        fetchMusic();
      } catch (error) {
        console.log(error);
      }
    }
    if (musicAction === "put") {
      try {
        console.log("PUT url:", `${API}/musictext/${musicEditId}`);
        console.log("PUT data:", { song: musicForm.song, artist: musicForm.artist, link: musicForm.link, category: musicForm.category });
        const res = await axios.put(`${API}/musictext/${musicEditId}`, fd, config);
        console.log("PUT success:", res.data);
        setMusicForm({ song: "", artist: "", link: "", category: "Bröllop", fil: "" });
        setMusicEditId(null);
        setMusicAction("send");
        fetchMusic();
      } catch (error) {
        console.log("PUT error status:", error.response?.status);
        console.log("PUT error data:", error.response?.data);
        console.log("PUT error url:", error.config?.url);
      }
    }
  }
  async function deleteMusic(m) {
    await axios.delete(`${API}/musictext/${m._id}`);
    fetchMusic();
  }
  function editMusic(m) {
    setMusicAction("put");
    setMusicEditId(m._id);
    setMusicForm({ song: m.song, artist: m.artist, link: m.link || "", category: m.category, fil: "" });
  }
  function cancelMusicEdit() {
    setMusicAction("send");
    setMusicEditId(null);
    setMusicForm({ song: "", artist: "", link: "", category: "Bröllop", fil: "" });
  }
  function playMusic(m) {
    if (playingId === m._id) {
      if (audioRef.current) {
        if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
        else { audioRef.current.play(); setIsPlaying(true); }
      }
      return;
    }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    const src = m.filename ? `${API}/uploads/${m.filename}` : m.link;
    if (!src) return;
    if (!audioRef.current) audioRef.current = new Audio();
    audioRef.current.src = src;
    audioRef.current.onloadedmetadata = () => setDuration(audioRef.current.duration);
    audioRef.current.ontimeupdate = () => setCurrentTime(audioRef.current.currentTime);
    audioRef.current.onended = () => { setPlayingId(null); setIsPlaying(false); setCurrentTime(0); };
    audioRef.current.play();
    setPlayingId(m._id);
    setIsPlaying(true);
  }
  function fmt(t) {
    if (!t || isNaN(t)) return "0:00";
    return `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, "0")}`;
  }

  // ── PLATSER ──
  async function fetchMaps() {
    const { data } = await axios.get(`${API}/maps`);
    setMaps(data);
  }
  async function submitMap(e) {
    e.preventDefault();
    if (!mapForm.latitude || !mapForm.longitude || !mapForm.destination) return;
    await axios.post(`${API}/maps`, mapForm);
    setMapForm({ latitude: "", longitude: "", destination: "" });
    fetchMaps();
  }
  async function deleteMap(m) {
    await axios.delete(`${API}/maps/${m._id}`);
    fetchMaps();
  }

  function logout() {
    localStorage.removeItem("token");
    navigate("/");
  }

  if (!user) return null;

  const unhandledCount = bookings.filter((b) => b.isDone === null || b.isDone === undefined).length;
  const acceptedCount = bookings.filter((b) => b.isDone === false).length;
  const doneCount = bookings.filter((b) => b.isDone === true).length;
  const pendingCount = unhandledCount + acceptedCount;
  const thisYear = new Date().getFullYear().toString();
  const statsThisYear = stats.filter((s) => s.date?.startsWith(thisYear)).length;
  const filteredMusic = music.filter((m) => m.category === musicCat);

  const tabs = [
    { id: "bokningar", label: "Bokningar", icon: "📋" },
    { id: "kalender", label: "Kalender", icon: "📅" },
    { id: "media", label: "Media", icon: "🎬" },
    { id: "citat", label: "Citat", icon: "💬" },
    { id: "later", label: "Låtar", icon: "🎵" },
    { id: "platser", label: "Platser", icon: "📍" },
    { id: "statistik", label: "Statistik", icon: "📊" },
  ];

  return (
    <div style={s.root}>
      {/* ── SIDEBAR ── */}
      <aside style={s.sidebar}>
        <div style={s.sidebarTop}>
          <div style={s.sidebarBrand}>Admin</div>
          <div style={s.sidebarSub}>Ellinor Nielsen</div>
        </div>
        <nav style={s.nav}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ ...s.navBtn, ...(tab === t.id ? s.navBtnActive : {}) }}>
              <span style={s.navIcon}>{t.icon}</span>
              <span>{t.label}</span>
              {t.id === "bokningar" && pendingCount > 0 && (
                <span style={s.badge}>{pendingCount}</span>
              )}
            </button>
          ))}
        </nav>
        <button onClick={logout} style={s.logoutBtn}>Logga ut</button>
      </aside>

      {/* ── MAIN ── */}
      <main style={s.main}>

        {/* ════════════ BOKNINGAR ════════════ */}
        {tab === "bokningar" && (
          <div>
            <h1 style={s.pageTitle}>Bokningar</h1>

            {/* Stats cards */}
            <div style={s.statsRow}>
              <StatCard label="Ej hanterade" value={unhandledCount} color={C.yellow} />
              <StatCard label="Accepterade" value={acceptedCount} color="#4a90a4" />
              <StatCard label="Klara" value={doneCount} color={C.green} />
              <StatCard label={`Arkiverade ${thisYear}`} value={statsThisYear} color={C.accent} />
            </div>

            {/* Booking table */}
            <div style={s.card}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>Aktiva förfrågningar</span>
                <span style={{ ...s.pill, background: C.accentFaint, color: C.accentLight }}>{bookings.length} st</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      {["Namn", "E-post", "Adress", "Telefon", "Datum", "Tid från", "Tid till", "Meddelande", "Status", ""].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length === 0 && (
                      <tr><td colSpan={10} style={{ ...s.td, textAlign: "center", color: C.muted }}>Inga bokningar</td></tr>
                    )}
                    {bookings.map((b) => (
                      <tr key={b._id} style={s.tr}>
                        <td style={s.td}>{b.förnamn} {b.efternamn}</td>
                        <td style={s.td}>{b.epost}</td>
                        <td style={s.td}>{b.adress}</td>
                        <td style={s.td}>{b.telefon}</td>
                        <td style={s.td}>{b.datum}</td>
                        <td style={s.td}>{b.tidFrån}</td>
                        <td style={s.td}>{b.tidTill}</td>
                        <td style={s.td}>
                          <div style={{ maxHeight: 80, overflowY: "auto", minWidth: 160, maxWidth: 260, fontSize: 13, color: C.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {b.text || <span style={{ color: C.muted }}>—</span>}
                          </div>
                        </td>
                        <td style={s.td}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              onClick={() => setBookingStatus(b, null)}
                              style={{ ...s.statusBtn, background: (b.isDone === null || b.isDone === undefined) ? C.yellow : "transparent", color: (b.isDone === null || b.isDone === undefined) ? "#fff" : C.muted, border: `1px solid ${(b.isDone === null || b.isDone === undefined) ? C.yellow : C.border}` }}
                              title="Ej hanterad"
                            >?</button>
                            <button
                              onClick={() => setBookingStatus(b, false)}
                              style={{ ...s.statusBtn, background: b.isDone === false ? "#4a90a4" : "transparent", color: b.isDone === false ? "#fff" : C.muted, border: `1px solid ${b.isDone === false ? "#4a90a4" : C.border}` }}
                              title="Acceptera"
                            >✓</button>
                            <button
                              onClick={() => setBookingStatus(b, true)}
                              style={{ ...s.statusBtn, background: b.isDone === true ? C.green : "transparent", color: b.isDone === true ? "#fff" : C.muted, border: `1px solid ${b.isDone === true ? C.green : C.border}` }}
                              title="Klar"
                            >★</button>
                          </div>
                        </td>
                        <td style={s.td}>
                          <button onClick={() => deleteBooking(b)} style={s.iconBtn} title={b.isDone === true ? "Arkivera & ta bort" : "Ta bort"}>
                            {b.isDone === true ? "📁" : "✕"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stats archive */}
            {(stats.length > 0 || nekadStats.length > 0) && (
              <div style={{ display: "flex", gap: 24, marginTop: 24, flexWrap: "wrap" }}>
                {stats.length > 0 && (
                  <div style={{ ...s.card, flex: 1, minWidth: 260 }}>
                    <div style={s.cardHeader}>
                      <span style={s.cardTitle}>Spelningsstatistik per år</span>
                    </div>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>År</th>
                          <th style={s.th}>Antal spelningar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...new Set(stats.map((s) => s.date?.slice(0, 4)).filter(Boolean))].sort().reverse().map((yr) => (
                          <tr key={yr} style={s.tr}>
                            <td style={s.td}>{yr}</td>
                            <td style={s.td}>{stats.filter((s) => s.date?.startsWith(yr)).length}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {nekadStats.length > 0 && (
                  <div style={{ ...s.card, flex: 1, minWidth: 260 }}>
                    <div style={s.cardHeader}>
                      <span style={s.cardTitle}>Nekade spelningar per år</span>
                    </div>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>År</th>
                          <th style={s.th}>Antal nekade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...new Set(nekadStats.map((n) => n.date?.slice(0, 4)).filter(Boolean))].sort().reverse().map((yr) => (
                          <tr key={yr} style={s.tr}>
                            <td style={s.td}>{yr}</td>
                            <td style={{ ...s.td, color: C.red, fontWeight: 600 }}>{nekadStats.filter((n) => n.date?.startsWith(yr)).length}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ════════════ KALENDER ════════════ */}
        {tab === "kalender" && (() => {
          const { year, month } = calMonth;
          const monthNames = ["Januari","Februari","Mars","April","Maj","Juni","Juli","Augusti","September","Oktober","November","December"];
          const dayNames = ["Mån","Tis","Ons","Tor","Fre","Lör","Sön"];
          const firstDay = new Date(year, month, 1);
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          // Monday-based offset
          const startOffset = (firstDay.getDay() + 6) % 7;
          const cells = Array(startOffset).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
          while (cells.length % 7 !== 0) cells.push(null);

          const acceptedBookings = bookings.filter((b) => b.isDone === false);

          function parseDate(str) {
            if (!str) return null;
            const parts = str.split(/[-\/]/);
            if (parts.length === 3) {
              const [a, b, c] = parts.map(Number);
              if (a > 1000) return new Date(a, b - 1, c);
              return new Date(c, b - 1, a);
            }
            return null;
          }

          function timeToMinutes(t) {
            if (!t) return null;
            const match = t.match(/^(\d{1,2}):(\d{2})/);
            if (!match) return null;
            return parseInt(match[1]) * 60 + parseInt(match[2]);
          }

          function bookingsOnDay(day) {
            return acceptedBookings
              .filter((b) => {
                const d = parseDate(b.datum);
                return d && d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
              })
              .sort((a, b) => {
                const ta = timeToMinutes(a.tidFrån);
                const tb = timeToMinutes(b.tidFrån);
                if (ta === null && tb === null) return 0;
                if (ta === null) return 1;
                if (tb === null) return -1;
                return ta - tb;
              });
          }

          async function saveCalTime(b) {
            const edit = calTimeEdit[b._id];
            if (!edit) return;
            const { _id, __v, ...rest } = b;
            await axios.put(`${API}/bokning/${b._id}`, { ...rest, tidFrån: edit.from, tidTill: edit.to });
            fetchBookings();
            setCalTimeEdit((p) => { const n = { ...p }; delete n[b._id]; return n; });
          }

          return (
            <div>
              <h1 style={s.pageTitle}>Kalender</h1>

              {/* Month nav */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                <button onClick={() => setCalMonth((p) => { const d = new Date(p.year, p.month - 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })} style={s.ghostBtn}>‹ Föregående</button>
                <span style={{ fontSize: 20, fontWeight: 700, color: C.text, minWidth: 180, textAlign: "center" }}>{monthNames[month]} {year}</span>
                <button onClick={() => setCalMonth((p) => { const d = new Date(p.year, p.month + 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })} style={s.ghostBtn}>Nästa ›</button>
              </div>

              {/* Legend */}
              <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 12, color: C.muted }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: "#4a90a4", display: "inline-block" }} />Accepterad (tid ej satt)</span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: C.accent, display: "inline-block" }} />Accepterad (tid satt)</span>
              </div>

              {/* Calendar grid */}
              <div style={{ ...s.card, padding: 0, overflow: "hidden" }}>
                {/* Day headers */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${C.border}` }}>
                  {dayNames.map((d) => (
                    <div key={d} style={{ padding: "10px 0", textAlign: "center", fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{d}</div>
                  ))}
                </div>
                {/* Weeks */}
                {Array.from({ length: cells.length / 7 }, (_, wi) => (
                  <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: wi < cells.length / 7 - 1 ? `1px solid ${C.border}` : "none" }}>
                    {cells.slice(wi * 7, wi * 7 + 7).map((day, di) => {
                      const dayBookings = day ? bookingsOnDay(day) : [];
                      const isToday = day && new Date().getFullYear() === year && new Date().getMonth() === month && new Date().getDate() === day;
                      return (
                        <div key={di} style={{ minHeight: 90, padding: "8px 6px", borderRight: di < 6 ? `1px solid ${C.border}` : "none", background: day ? "#fff" : "#faf6f2", position: "relative" }}>
                          {day && (
                            <>
                              <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 400, color: isToday ? C.accent : C.muted, marginBottom: 4, width: 24, height: 24, borderRadius: "50%", background: isToday ? C.accentFaint : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>{day}</div>
                              {dayBookings.map((b) => {
                                const hasTime = b.tidFrån && b.tidFrån !== "" && timeToMinutes(b.tidFrån) !== null;
                                const isExpanded = expandedBooking === b._id;
                                const isEditing = !!calTimeEdit[b._id];
                                const fromMin = hasTime ? timeToMinutes(b.tidFrån) : null;
                                const toMin = hasTime ? timeToMinutes(b.tidTill) : null;
                                const duration = (fromMin !== null && toMin !== null) ? toMin - fromMin : null;
                                return (
                                  <div key={b._id}>
                                    <div
                                      onClick={(e) => { if (isExpanded) { setExpandedBooking(null); } else { const r = e.currentTarget.getBoundingClientRect(); const popW = 284; const popH = 380; const x = Math.min(Math.max(r.right + 4, 8), window.innerWidth - popW - 8); const y = Math.min(Math.max(r.top, 8), window.innerHeight - popH - 8); setExpandedPos({ x, y }); setExpandedBooking(b._id); } }}
                                      style={{ background: hasTime ? C.accent : "#4a90a4", color: "#fff", borderRadius: 4, padding: "3px 6px", fontSize: 11, cursor: "pointer", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                      title={`${b.förnamn} ${b.efternamn}${hasTime ? ` · ${b.tidFrån}–${b.tidTill}` : ""}`}
                                    >
                                      {b.förnamn} {b.efternamn?.charAt(0)}.
                                      {hasTime && <span style={{ opacity: 0.8, marginLeft: 4 }}>{b.tidFrån}</span>}
                                    </div>
                                    {isExpanded && (
                                      <div style={{ position: "fixed", left: expandedPos.x, top: expandedPos.y, zIndex: 9999, width: 280, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", maxHeight: "90vh", overflowY: "auto" }}>
                                        <div style={{ fontWeight: 700, color: C.text, marginBottom: 8, fontSize: 14 }}>{b.förnamn} {b.efternamn}</div>
                                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{b.epost}</div>
                                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{b.telefon}</div>
                                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{b.adress}</div>
                                        <div style={{ fontSize: 12, color: C.muted, marginBottom: b.text ? 8 : 12 }}>{b.datum}</div>
                                        {b.text && (
                                          <div style={{ fontSize: 12, color: C.text, background: C.accentFaint, borderRadius: 6, padding: "8px 10px", marginBottom: 12, maxHeight: 100, overflowY: "auto", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{b.text}</div>
                                        )}
                                        {duration !== null && (
                                          <div style={{ background: C.accentFaint, borderRadius: 6, padding: "6px 10px", fontSize: 12, color: C.accent, marginBottom: 12 }}>
                                            {b.tidFrån} – {b.tidTill} ({Math.floor(duration / 60)}h{duration % 60 > 0 ? ` ${duration % 60}min` : ""})
                                          </div>
                                        )}
                                        {isEditing ? (
                                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                            <div style={{ display: "flex", gap: 8 }}>
                                              <div style={{ flex: 1 }}>
                                                <label style={s.label}>Från</label>
                                                <input type="time" value={calTimeEdit[b._id]?.from || ""} onChange={(e) => setCalTimeEdit((p) => ({ ...p, [b._id]: { ...p[b._id], from: e.target.value } }))} style={s.input} />
                                              </div>
                                              <div style={{ flex: 1 }}>
                                                <label style={s.label}>Till</label>
                                                <input type="time" value={calTimeEdit[b._id]?.to || ""} onChange={(e) => setCalTimeEdit((p) => ({ ...p, [b._id]: { ...p[b._id], to: e.target.value } }))} style={s.input} />
                                              </div>
                                            </div>
                                            <div style={{ display: "flex", gap: 8 }}>
                                              <button onClick={() => saveCalTime(b)} style={s.smallBtn}>Spara</button>
                                              <button onClick={() => setCalTimeEdit((p) => { const n = { ...p }; delete n[b._id]; return n; })} style={s.smallBtnGhost}>Avbryt</button>
                                            </div>
                                          </div>
                                        ) : (
                                          <button onClick={() => { setCalTimeEdit((p) => ({ ...p, [b._id]: { from: b.tidFrån || "", to: b.tidTill || "" } })); }} style={s.smallBtn}>{hasTime ? "Ändra tid" : "Sätt tid"}</button>
                                        )}
                                        <button onClick={() => setExpandedBooking(null)} style={{ ...s.smallBtnGhost, marginTop: 8, width: "100%" }}>Stäng</button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Accepted bookings list */}
              {acceptedBookings.length > 0 && (
                <div style={{ ...s.card, marginTop: 24 }}>
                  <div style={s.cardHeader}>
                    <span style={s.cardTitle}>Accepterade spelningar</span>
                    <span style={{ ...s.pill, background: "#e8f4f8", color: "#4a90a4" }}>{acceptedBookings.length} st</span>
                  </div>
                  <table style={s.table}>
                    <thead>
                      <tr>{["Namn","Datum","Adress","Tid",""].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {acceptedBookings.sort((a, b) => (a.datum || "").localeCompare(b.datum || "")).map((b) => (
                        <tr key={b._id} style={{ ...s.tr, ...(expandedBooking === b._id ? { background: C.accentFaint, outline: `2px solid ${C.accent}`, outlineOffset: -1 } : {}) }}>
                          <td style={s.td}>{b.förnamn} {b.efternamn}</td>
                          <td style={s.td}>{b.datum}</td>
                          <td style={s.td}>{b.adress}</td>
                          <td style={s.td}>{b.tidFrån && b.tidTill ? `${b.tidFrån}–${b.tidTill}` : <span style={{ color: C.muted, fontStyle: "italic" }}>Ej satt</span>}</td>
                          <td style={{ ...s.td, textAlign: "right" }}>
                            <button onClick={() => { setTab("kalender"); const d = parseDate(b.datum); if (d) setCalMonth({ year: d.getFullYear(), month: d.getMonth() }); setExpandedBooking(b._id); }} style={s.iconBtn} title="Visa i kalender">📅</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* ════════════ MEDIA ════════════ */}
        {tab === "media" && (
          <div>
            <h1 style={s.pageTitle}>Media</h1>

            {/* Upload form */}
            <div style={s.card}>
              <div style={s.cardHeader}><span style={s.cardTitle}>Ladda upp bild / video</span></div>
              <form onSubmit={submitUpload} style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
                <div>
                  <label style={s.label}>Välj fil</label>
                  <input type="file" accept="image/png,image/jpeg,video/mp4" onChange={handleUploadFile} style={s.fileInput} />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={s.label}>Bildtext (valfritt)</label>
                  <input value={uploadText} onChange={(e) => setUploadText(e.target.value)} placeholder="Skriv bildtext..." style={s.input} />
                </div>
                {(uploadPreviewImg || uploadPreviewVid) && (
                  <div style={{ width: "100%" }}>
                    {uploadPreviewImg && <img src={uploadPreviewImg} alt="" style={{ height: 100, borderRadius: 8, objectFit: "cover" }} />}
                    {uploadPreviewVid && <video src={uploadPreviewVid} style={{ height: 100, borderRadius: 8 }} muted />}
                  </div>
                )}
                <button type="submit" disabled={!uploadFile || uploadLoading} style={s.primaryBtn}>
                  {uploadLoading ? "Laddar upp..." : "Ladda upp"}
                </button>
              </form>
            </div>

            {/* Drag-and-drop */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>

                {/* Active */}
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <span style={s.cardTitle}>Visas på Lyssna</span>
                    <span style={{ ...s.pill, background: C.accentFaint, color: C.accentLight }}>{mediaActive.length} st</span>
                  </div>
                  <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Dra för att ändra ordning. Dra hit från "Ej aktiva" för att aktivera.</p>
                  <Droppable droppableId="active">
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} style={{ ...s.dropZone, background: snapshot.isDraggingOver ? C.accentFaint : "transparent" }}>
                        {mediaActive.map((item, idx) => (
                          <Draggable key={item.filename} draggableId={item.filename} index={idx}>
                            {(prov, snap) => (
                              <div ref={prov.innerRef} {...prov.draggableProps} style={{ ...s.mediaRow, ...(snap.isDragging ? s.mediaRowDragging : {}), ...prov.draggableProps.style }}>
                                <span {...prov.dragHandleProps} style={s.dragHandle} title="Dra för att flytta">⠿</span>
                                <MediaThumb item={item} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {editCaptionId === item.filename ? (
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <input value={editCaptionText} onChange={(e) => setEditCaptionText(e.target.value)} style={{ ...s.input, flex: 1 }} />
                                      <button onClick={() => saveCaption(item)} style={s.smallBtn}>Spara</button>
                                      <button onClick={() => setEditCaptionId(null)} style={s.smallBtnGhost}>Avbryt</button>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                      <span style={{ color: C.muted, fontSize: 13, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {item.text && item.text !== "null" ? item.text : <em>Ingen text</em>}
                                      </span>
                                      <button onClick={() => { setEditCaptionId(item.filename); setEditCaptionText(item.text === "null" ? "" : item.text || ""); }} style={s.iconBtn} title="Ändra bildtext">✎</button>
                                    </div>
                                  )}
                                  <span style={{ fontSize: 11, color: C.muted }}>{item.mimetype}</span>
                                </div>
                                <button onClick={() => deleteMedia(item)} style={{ ...s.iconBtn, color: C.red }} title="Ta bort">✕</button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {mediaActive.length === 0 && <p style={{ color: C.muted, textAlign: "center", padding: 24 }}>Tom</p>}
                      </div>
                    )}
                  </Droppable>
                </div>

                {/* Inactive */}
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <span style={s.cardTitle}>Ej aktiva</span>
                    <span style={{ ...s.pill, background: "rgba(255,255,255,0.06)", color: C.muted }}>{mediaInactive.length} st</span>
                  </div>
                  <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Dra till "Visas på Lyssna" för att aktivera.</p>
                  <Droppable droppableId="inactive">
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} style={{ ...s.dropZone, background: snapshot.isDraggingOver ? C.accentFaint : "transparent" }}>
                        {mediaInactive.map((item, idx) => (
                          <Draggable key={item.filename} draggableId={item.filename} index={idx}>
                            {(prov, snap) => (
                              <div ref={prov.innerRef} {...prov.draggableProps} style={{ ...s.mediaRow, ...(snap.isDragging ? s.mediaRowDragging : {}), ...prov.draggableProps.style }}>
                                <span {...prov.dragHandleProps} style={s.dragHandle}>⠿</span>
                                <MediaThumb item={item} />
                                <span style={{ color: C.muted, fontSize: 13, flex: 1 }}>{item.filename}</span>
                                <button onClick={() => deleteMedia(item)} style={{ ...s.iconBtn, color: C.red }} title="Ta bort">✕</button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {mediaInactive.length === 0 && <p style={{ color: C.muted, textAlign: "center", padding: 24 }}>Alla filer är aktiva</p>}
                      </div>
                    )}
                  </Droppable>
                </div>

              </div>
            </DragDropContext>
          </div>
        )}

        {/* ════════════ CITAT ════════════ */}
        {tab === "citat" && (
          <div>
            <h1 style={s.pageTitle}>Citat</h1>
            <div style={s.card}>
              <div style={s.cardHeader}><span style={s.cardTitle}>{citatAction === "send" ? "Lägg till citat" : "Redigera citat"}</span></div>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <textarea
                  value={citatText}
                  onChange={(e) => setCitatText(e.target.value)}
                  placeholder='Skriv ett citat, t.ex. "Ellinor sjöng vackert på vårt bröllop!"'
                  style={{ ...s.input, flex: 1, minHeight: 80, resize: "vertical" }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button onClick={submitCitat} disabled={!citatText.trim()} style={s.primaryBtn}>
                    {citatAction === "send" ? "Lägg till" : "Spara"}
                  </button>
                  {citatAction === "put" && (
                    <button onClick={cancelCitatEdit} style={s.ghostBtn}>Avbryt</button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ ...s.card, marginTop: 24 }}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>Alla citat</span>
                <span style={{ ...s.pill, background: C.accentFaint, color: C.accentLight }}>{citat.length} st</span>
              </div>
              {citat.length === 0 && <p style={{ color: C.muted }}>Inga citat ännu.</p>}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {citat.map((c) => (
                  <div key={c._id} style={{ ...s.citatRow, ...(citatEditId === c._id ? { borderColor: C.accent } : {}) }}>
                    <p style={{ margin: 0, flex: 1, color: C.text, lineHeight: 1.6 }}>"{c.text}"</p>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button onClick={() => editCitat(c)} style={s.iconBtn} title="Redigera">✎</button>
                      <button onClick={() => deleteCitat(c)} style={{ ...s.iconBtn, color: C.red }} title="Ta bort">✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ════════════ LÅTAR ════════════ */}
        {tab === "later" && (
          <div>
            <h1 style={s.pageTitle}>Låtar</h1>

            {/* Audio player */}
            {playingId && (
              <div style={s.audioPlayer}>
                <span style={{ color: C.accentLight, fontWeight: 600 }}>
                  {music.find((m) => m._id === playingId)?.song} — {music.find((m) => m._id === playingId)?.artist}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                  <button onClick={() => {
                    if (audioRef.current) { if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); } else { audioRef.current.play(); setIsPlaying(true); } }
                  }} style={{ ...s.primaryBtn, padding: "6px 16px" }}>
                    {isPlaying ? "⏸" : "▶"}
                  </button>
                  <span style={{ color: C.muted, fontSize: 13 }}>{fmt(currentTime)}</span>
                  <input type="range" min={0} max={duration || 1} value={currentTime}
                    onChange={(e) => { if (audioRef.current) { audioRef.current.currentTime = e.target.value; setCurrentTime(Number(e.target.value)); } }}
                    style={{ flex: 1, accentColor: C.accent }}
                  />
                  <span style={{ color: C.muted, fontSize: 13 }}>{fmt(duration)}</span>
                  <button onClick={() => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; } setPlayingId(null); setIsPlaying(false); }} style={s.iconBtn}>✕</button>
                </div>
              </div>
            )}

            {/* Form */}
            <div style={s.card}>
              <div style={s.cardHeader}><span style={s.cardTitle}>{musicAction === "send" ? "Lägg till låt" : "Redigera låt"}</span></div>
              <form onSubmit={submitMusic} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={s.label}>Låttitel *</label>
                  <input name="song" value={musicForm.song} onChange={handleMusicFormChange} placeholder="t.ex. Perfect" style={s.input} required />
                </div>
                <div>
                  <label style={s.label}>Artist *</label>
                  <input name="artist" value={musicForm.artist} onChange={handleMusicFormChange} placeholder="t.ex. Ed Sheeran" style={s.input} required />
                </div>
                <div>
                  <label style={s.label}>Länk (valfritt)</label>
                  <input name="link" value={musicForm.link} onChange={handleMusicFormChange} placeholder="https://..." style={s.input} />
                </div>
                <div>
                  <label style={s.label}>Kategori</label>
                  <select name="category" value={musicForm.category} onChange={handleMusicFormChange} style={s.input}>
                    <option>Bröllop</option>
                    <option>Dop</option>
                    <option>Begravning</option>
                  </select>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={s.label}>Ljudfil (valfritt, mp3/video)</label>
                  <input type="file" name="fil" accept="audio/mp3,audio/mpeg,video/*" onChange={handleMusicFormChange} style={s.fileInput} />
                </div>
                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
                  <button type="submit" disabled={!musicForm.song || !musicForm.artist} style={s.primaryBtn}>
                    {musicAction === "send" ? "Lägg till" : "Spara ändringar"}
                  </button>
                  {musicAction === "put" && (
                    <button type="button" onClick={cancelMusicEdit} style={s.ghostBtn}>Avbryt</button>
                  )}
                </div>
              </form>
            </div>

            {/* Category tabs + table */}
            <div style={{ ...s.card, marginTop: 24 }}>
              <div style={s.cardHeader}><span style={s.cardTitle}>Låtlista</span></div>
              <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {["Bröllop", "Dop", "Begravning"].map((cat) => (
                  <button key={cat} onClick={() => setMusicCat(cat)} style={{ ...s.catBtn, ...(musicCat === cat ? s.catBtnActive : {}) }}>
                    {cat} <span style={{ color: C.muted, marginLeft: 4, fontSize: 12 }}>({music.filter((m) => m.category === cat).length})</span>
                  </button>
                ))}
              </div>
              <table style={s.table}>
                <thead>
                  <tr>
                    {["Låt", "Artist", "Länk / Fil", ""].map((h) => <th key={h} style={s.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredMusic.length === 0 && (
                    <tr><td colSpan={4} style={{ ...s.td, textAlign: "center", color: C.muted }}>Inga låtar i denna kategori</td></tr>
                  )}
                  {filteredMusic.map((m) => (
                    <tr key={m._id} style={{ ...s.tr, ...(musicEditId === m._id ? { background: C.accentFaint } : {}) }}>
                      <td style={s.td}>{m.song}</td>
                      <td style={s.td}>{m.artist}</td>
                      <td style={s.td}>
                        {(m.filename || m.link) ? (
                          <button onClick={() => playMusic(m)} style={{ ...s.iconBtn, color: playingId === m._id && isPlaying ? C.accentLight : C.muted }}>
                            {playingId === m._id && isPlaying ? "⏸ Spelas" : "▶ Spela"}
                          </button>
                        ) : <span style={{ color: C.muted, fontSize: 13 }}>—</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: "right" }}>
                        <button onClick={() => editMusic(m)} style={s.iconBtn} title="Redigera">✎</button>
                        <button onClick={() => deleteMusic(m)} style={{ ...s.iconBtn, color: C.red, marginLeft: 8 }} title="Ta bort">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════════════ PLATSER ════════════ */}
        {tab === "platser" && (
          <div>
            <h1 style={s.pageTitle}>Platser på kartan</h1>
            <div style={s.card}>
              <div style={s.cardHeader}><span style={s.cardTitle}>Lägg till plats</span></div>
              <form onSubmit={submitMap} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 16, alignItems: "flex-end" }}>
                <div>
                  <label style={s.label}>Latitud</label>
                  <input value={mapForm.latitude} onChange={(e) => setMapForm((p) => ({ ...p, latitude: e.target.value }))} placeholder="55.4807143" style={s.input} required />
                </div>
                <div>
                  <label style={s.label}>Longitud</label>
                  <input value={mapForm.longitude} onChange={(e) => setMapForm((p) => ({ ...p, longitude: e.target.value }))} placeholder="13.4859646" style={s.input} required />
                </div>
                <div>
                  <label style={s.label}>Destination</label>
                  <input value={mapForm.destination} onChange={(e) => setMapForm((p) => ({ ...p, destination: e.target.value }))} placeholder="t.ex. Malmö" style={s.input} required />
                </div>
                <button type="submit" style={s.primaryBtn}>Lägg till</button>
              </form>
            </div>

            <div style={{ ...s.card, marginTop: 24 }}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>Sparade platser</span>
                <span style={{ ...s.pill, background: C.accentFaint, color: C.accentLight }}>{maps.length} st</span>
              </div>
              <table style={s.table}>
                <thead>
                  <tr>
                    {["Destination", "Latitud", "Longitud", ""].map((h) => <th key={h} style={s.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {maps.length === 0 && (
                    <tr><td colSpan={4} style={{ ...s.td, textAlign: "center", color: C.muted }}>Inga platser sparade</td></tr>
                  )}
                  {maps.map((m) => (
                    <tr key={m._id} style={s.tr}>
                      <td style={{ ...s.td, fontWeight: 500 }}>{m.destination}</td>
                      <td style={s.td}>{m.latitude}</td>
                      <td style={s.td}>{m.longitude}</td>
                      <td style={{ ...s.td, textAlign: "right" }}>
                        <button onClick={() => deleteMap(m)} style={{ ...s.iconBtn, color: C.red }} title="Ta bort">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════════════ STATISTIK ════════════ */}
        {tab === "statistik" && (
          <div>
            <h1 style={s.pageTitle}>Statistik</h1>

            {/* Totals */}
            <div style={s.statsRow}>
              <StatCard label="Besök totalt" value={visitorStats?.totalVisits ?? "—"} color={C.accent} />
              <StatCard label="Unika besökare totalt" value={visitorStats?.totalUnique ?? "—"} color={C.blue} />
              <StatCard
                label={`Besök ${new Date().toLocaleString("sv-SE", { month: "long", year: "numeric" })}`}
                value={visitorStats?.byMonth.find((m) => m.month === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`)?.visits ?? 0}
                color={C.accentLight}
              />
              <StatCard
                label={`Unika ${new Date().toLocaleString("sv-SE", { month: "long", year: "numeric" })}`}
                value={visitorStats?.byMonth.find((m) => m.month === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`)?.unique ?? 0}
                color={C.green}
              />
            </div>

            {/* Per month table */}
            <div style={s.card}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>Besök per månad</span>
                <span style={{ ...s.pill, background: C.accentFaint, color: C.accentLight }}>
                  {visitorStats?.byMonth.length ?? 0} månader
                </span>
              </div>
              {!visitorStats && <p style={{ color: C.muted }}>Laddar...</p>}
              {visitorStats && visitorStats.byMonth.length === 0 && (
                <p style={{ color: C.muted }}>Inga besök registrerade ännu.</p>
              )}
              {visitorStats && visitorStats.byMonth.length > 0 && (
                <table style={s.table}>
                  <thead>
                    <tr>
                      {["Månad", "Besök totalt", "Unika besökare"].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...visitorStats.byMonth].reverse().map((row) => {
                      const [yr, mo] = row.month.split("-");
                      const label = new Date(Number(yr), Number(mo) - 1, 1).toLocaleString("sv-SE", { month: "long", year: "numeric" });
                      return (
                        <tr key={row.month} style={s.tr}>
                          <td style={{ ...s.td, fontWeight: 500, textTransform: "capitalize" }}>{label}</td>
                          <td style={s.td}>{row.visits}</td>
                          <td style={s.td}>{row.unique}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px", flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 32, fontWeight: 700, color }}>{value}</div>
      <div style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function MediaThumb({ item }) {
  const src = `https://ellinornielsen-backend.onrender.com/api/uploads/${item.filename}`;
  if (item.mimetype === "video/mp4") {
    return <video src={src} style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} muted />;
  }
  return <img src={src} alt="" style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />;
}

const s = {
  root: { display: "flex", position: "fixed", inset: 0, zIndex: 200, background: C.bg, color: C.text, fontFamily: "helvetica, sans-serif", overflow: "hidden" },
  sidebar: { width: 220, background: C.sidebar, display: "flex", flexDirection: "column", flexShrink: 0, height: "100%", boxShadow: "2px 0 12px rgba(0,0,0,0.12)" },
  sidebarTop: { padding: "32px 24px 24px" },
  sidebarBrand: { fontSize: 22, fontWeight: 700, color: "#fff", letterSpacing: 1 },
  sidebarSub: { fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 4 },
  nav: { flex: 1, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 4 },
  navBtn: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 8, border: "none", background: "transparent", color: "rgba(255,255,255,0.65)", cursor: "pointer", fontSize: 14, width: "100%", textAlign: "left", transition: "all 0.15s" },
  navBtnActive: { background: "rgba(255,255,255,0.18)", color: "#fff" },
  navIcon: { fontSize: 17, width: 20, textAlign: "center" },
  badge: { marginLeft: "auto", background: "#fff", color: C.accent, borderRadius: 99, fontSize: 11, padding: "1px 7px", fontWeight: 700 },
  logoutBtn: { margin: "12px", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "transparent", color: "rgba(255,255,255,0.65)", cursor: "pointer", fontSize: 13 },
  main: { flex: 1, padding: "36px 40px", overflowY: "auto", height: "100%" },
  pageTitle: { fontSize: 26, fontWeight: 700, color: C.text, marginBottom: 24, marginTop: 0, fontFamily: "helvetica" },
  statsRow: { display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" },
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, boxShadow: "0 1px 4px rgba(129,86,65,0.07)" },
  cardHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 600, color: C.text },
  pill: { fontSize: 12, borderRadius: 99, padding: "2px 10px", fontWeight: 500 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "10px 12px", fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${C.border}` },
  tr: { borderBottom: `1px solid ${C.border}`, transition: "background 0.1s" },
  td: { padding: "12px 12px", fontSize: 14, color: C.text, verticalAlign: "middle" },
  statusBtn: { border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  iconBtn: { background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 16, padding: "4px 6px", borderRadius: 4 },
  label: { display: "block", fontSize: 12, color: C.muted, marginBottom: 6, fontWeight: 500 },
  input: { width: "100%", background: "#faf6f2", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" },
  fileInput: { color: C.muted, fontSize: 13 },
  primaryBtn: { background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  ghostBtn: { background: "transparent", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: "pointer" },
  smallBtn: { background: C.accent, color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer" },
  smallBtnGhost: { background: "transparent", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer" },
  dropZone: { minHeight: 80, borderRadius: 8, transition: "background 0.15s", display: "flex", flexDirection: "column", gap: 8 },
  mediaRow: { display: "flex", alignItems: "center", gap: 12, background: "#fdf8f4", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" },
  mediaRowDragging: { boxShadow: "0 8px 24px rgba(129,86,65,0.18)", background: "#f5ede4" },
  dragHandle: { cursor: "grab", color: C.muted, fontSize: 18, userSelect: "none", flexShrink: 0 },
  catBtn: { border: `1px solid ${C.border}`, background: "transparent", color: C.muted, borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" },
  catBtnActive: { background: C.accentFaint, borderColor: C.accent, color: C.accent, fontWeight: 600 },
  citatRow: { display: "flex", alignItems: "flex-start", gap: 16, background: "#fdf8f4", border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px" },
  audioPlayer: { background: C.accentFaint, border: `1px solid ${C.accent}`, borderRadius: 10, padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" },
};

export default AdminPage;
